import type {
  OpticalMeasureFramingDistanceProfileSettings,
  OpticalMeasureFramingProfile,
  OpticalMeasureFramingSettings,
} from './ExpoOpticalMeasure.types'

type WarningLogger = {
  warn: (message: string) => void
}

export type OpticalMeasureDistanceRange = {
  min: number
  max: number
}

const isPositiveFinite = (value: number) => Number.isFinite(value) && value > 0

const isNormalized = (value: number) =>
  Number.isFinite(value) && value >= 0 && value <= 1

const isValidDistanceProfile = (
  profile: OpticalMeasureFramingDistanceProfileSettings
) =>
  isPositiveFinite(profile.preferredDistanceCm) &&
  isPositiveFinite(profile.guidanceDistanceToleranceCm) &&
  isPositiveFinite(profile.captureDistanceToleranceCm) &&
  profile.captureDistanceToleranceCm >= profile.guidanceDistanceToleranceCm

export const resolveOpticalMeasureFramingSettings = (
  framing: OpticalMeasureFramingSettings,
  logger: WarningLogger = console
): OpticalMeasureFramingSettings | null => {
  if (
    !framing ||
    !framing.normal ||
    !framing.vbox ||
    !framing.targetCenter ||
    !framing.faceFit ||
    !framing.position ||
    !framing.enforcement
  ) {
    logger.warn(
      '[optical-measure] Missing framing settings; retaining safe fallback behavior.'
    )
    return null
  }

  const isDistanceValid =
    isValidDistanceProfile(framing.normal) &&
    isValidDistanceProfile(framing.vbox)
  const isTargetValid =
    isNormalized(framing.targetCenter.x) && isNormalized(framing.targetCenter.y)
  const isFaceFitValid =
    (framing.faceFit.mode === 'session-calibrated' ||
      framing.faceFit.mode === 'fallback') &&
    isPositiveFinite(framing.faceFit.fallbackFaceWidthMm) &&
    isPositiveFinite(framing.faceFit.fallbackFaceHeightMm) &&
    isPositiveFinite(framing.faceFit.ovalPaddingFactor) &&
    isPositiveFinite(framing.faceFit.minScaleFactor) &&
    isPositiveFinite(framing.faceFit.maxScaleFactor) &&
    framing.faceFit.minScaleFactor <= framing.faceFit.maxScaleFactor &&
    Number.isInteger(framing.faceFit.stableFrameCount) &&
    framing.faceFit.stableFrameCount > 0 &&
    isPositiveFinite(framing.faceFit.timeoutMs)
  const isPositionValid =
    isPositiveFinite(framing.position.guidanceToleranceXFactor) &&
    isPositiveFinite(framing.position.guidanceToleranceYFactor) &&
    isPositiveFinite(framing.position.captureToleranceXFactor) &&
    isPositiveFinite(framing.position.captureToleranceYFactor) &&
    isPositiveFinite(framing.position.guidanceSizeToleranceFactor) &&
    isPositiveFinite(framing.position.captureSizeToleranceFactor) &&
    framing.position.captureToleranceXFactor >=
      framing.position.guidanceToleranceXFactor &&
    framing.position.captureToleranceYFactor >=
      framing.position.guidanceToleranceYFactor &&
    framing.position.captureSizeToleranceFactor >=
      framing.position.guidanceSizeToleranceFactor

  if (isDistanceValid && isTargetValid && isFaceFitValid && isPositionValid) {
    return framing
  }

  logger.warn(
    '[optical-measure] Invalid framing settings; retaining safe fallback behavior.'
  )
  return null
}

export const resolveOpticalMeasureFramingProfile = (
  framing: OpticalMeasureFramingSettings,
  profile: OpticalMeasureFramingProfile,
  logger: WarningLogger = console
): OpticalMeasureFramingDistanceProfileSettings | null =>
  resolveOpticalMeasureFramingSettings(framing, logger)?.[profile] ?? null

export const getOpticalMeasureDistanceRange = (
  profile: OpticalMeasureFramingDistanceProfileSettings,
  tolerance: 'guidance' | 'capture'
): OpticalMeasureDistanceRange => {
  const distanceTolerance =
    tolerance === 'guidance'
      ? profile.guidanceDistanceToleranceCm
      : profile.captureDistanceToleranceCm

  return {
    min: profile.preferredDistanceCm - distanceTolerance,
    max: profile.preferredDistanceCm + distanceTolerance,
  }
}
